import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import Dialog from "components/DesignComponents/Dialog";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import CompanyDropdown from "components/QueryDropdown/CompanyDropdown";
import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import { ENDPOINTS, Query, useApi, useMediaQueries } from "shared";

import { COMPANY_ACTIONS, DialogProps } from "../../../types";
import DialogActions from "../../components/DialogActions";

const CreateCompany = ({ isOpen, close }: DialogProps) => {
    const { user } = useContext(AuthContext);
    const { triggerRootCompany } = useContext(ParamsContext);

    const { t } = useTranslation();
    const { postData, handleResponse } = useApi();
    const { toMd } = useMediaQueries();

    const [dropdownValue, setDropdownValue] = useState<Query | null>(null);
    const [newCompanyName, setNewCompanyName] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const response = await postData(ENDPOINTS.Companies, {
                name: newCompanyName,
                company_id: dropdownValue?.id
            });

            handleCloseDialog();
            triggerRootCompany(true);
            handleResponse(response);
        } catch (error) {
            handleResponse(error);
        }

        setIsLoading(false);
    };

    const handleDropdownChange = (companyValue: Query | null) =>
        setDropdownValue(companyValue);

    const changeValue = (e: { target: { value: string } }) =>
        setNewCompanyName(e.target.value);

    const handleCloseDialog = () => close(COMPANY_ACTIONS.CreateCompany);

    return (
        <Dialog
            title={t("Dialog##create company")}
            description={t("Dialog##create company description")}
            isTitleSeparator
            isActionsSeparator
            open={isOpen}
            close={handleCloseDialog}
            submit={handleSubmit}
            slotProps={{
                transition: {
                    onExited: () => {
                        setDropdownValue(null);
                        setNewCompanyName("");
                    }
                }
            }}
            actions={
                <DialogActions
                    onClose={handleCloseDialog}
                    isLoading={isLoading}
                    isReadyToConfirm={dropdownValue && newCompanyName.trim()}
                    confirmationButtonText={t("Button##create")}
                    cancelButtonTestId="cancel-create-company"
                    confirmationButtonTestId="confirm-create-company"
                />
            }
            extendToMaxWidth={toMd}
        >
            <CompanyDropdown
                data-testid="create-company-query-dropdown"
                value={dropdownValue}
                isRequired
                readOnly={isLoading}
                change={(_, val) => handleDropdownChange(val)}
                labelLeft={t("Dialog##parent company")}
                customStyle={{ marginBottom: "16px" }}
                isRootRequired
            />

            <InputField
                data-testid="company-name-input-field"
                size={INPUT_FIELD_SIZES.Medium}
                fullWidth
                required
                readOnly={isLoading}
                onChange={changeValue}
                labelLeft={t("Dialog##company name")}
                placeholder={`${t("Dialog##example")} ${user?.company.name}`}
            />
        </Dialog>
    );
};

export default CreateCompany;
